import { useState, useEffect, useCallback } from "react";
import { FiSearch, FiBell as FiDumbbell, FiChevronDown } from "react-icons/fi";
import Modal from "../../../../tailadmin/components/ui/Modal";
import { Exercise } from "../../../../types";
import exercicies from "../../../../services/exercicies";
import utils from "../../../../helpers/utils";
import loadingImage from "../../../../assets/loading.png";

interface ExerciseSelectProps {
  professionalId: string;
  value: string;
  onChange: (name: string, id: number, exercise?: Exercise) => void;
}

export const ExerciseSelect = ({
  professionalId,
  value,
  onChange,
}: ExerciseSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const data = await exercicies.list_all(professionalId, 1, 30, query);
      setResults(data);
    } catch (error) {
      console.error("Erro ao buscar exercícios:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [professionalId, query]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isOpen) {
        fetchExercises();
      }
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [isOpen, query, fetchExercises]);

  const handleSelect = (ex: Exercise) => {
    onChange(ex.name, Number(ex.id), ex);
    setIsOpen(false);
    setQuery("");
  };

  const handleClose = () => {
    setIsOpen(false);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange("", 0);
  };

  const placeholder = "Buscar exercício...";
  const hasValue = value.length > 0;

  const getEmptyMessage = () => {
    if (query.length > 0) return "Nenhum exercício encontrado para esta busca.";
    return "Digite na busca para listar exercícios.";
  };

  const getMediaUrl = (ex: Exercise) => {
    // Prioriza image_url, depois image, e por último usa getUriImage com fallback
    if (ex.image_url && ex.image_url.trim() !== "") {
      return ex.image_url;
    }
    if (ex.image && ex.image.trim() !== "") {
      return ex.image;
    }
    return utils.getUriImage(ex.image ?? loadingImage, "all_view");
  };

  const getVideoUrl = (ex: Exercise) => {
    if (!ex.video) return null;
    if (ex.video.startsWith("data:video")) {
      return ex.video;
    }
    if (ex.video.trim() !== "") {
      return utils.getUriImage(
        ex.video,
        ex.professional_id ? String(ex.professional_id) : "all_view"
      );
    }
    return null;
  };

  const isVideoUrl = (url: string | null): boolean => {
    if (!url) return false;
    if (url.startsWith("data:video")) return true;
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
    return videoExtensions.some((ext) => url.toLowerCase().includes(ext));
  };

  const renderExerciseMedia = (ex: Exercise) => {
    const videoUrl = getVideoUrl(ex);
    const imageUrl = getMediaUrl(ex);
    const hasVideo = isVideoUrl(videoUrl);

    if (hasVideo) {
      return (
        <video
          src={videoUrl!}
          className="w-full h-full object-cover"
          muted
          playsInline
        />
      );
    }
    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt={ex.name}
          className="w-full h-full object-cover"
        />
      );
    }
    return <FiDumbbell className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />;
  };

  const renderListContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 gap-3">
          <div className="h-8 w-8 sm:h-10 sm:w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Buscando exercícios...
          </span>
        </div>
      );
    }
    if (results.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 text-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
            <FiDumbbell className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {getEmptyMessage()}
          </p>
        </div>
      );
    }
    return (
      <div className="py-2 sm:py-3">
        {results.map((ex) => (
          <button
            key={ex.id}
            type="button"
            onClick={() => handleSelect(ex)}
            className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 text-left transition active:bg-purple-50 md:hover:bg-purple-50 dark:text-white dark:active:bg-purple-900/20 md:dark:hover:bg-purple-900/20 border-b border-gray-100 last:border-0 dark:border-gray-800 touch-manipulation"
          >
            {/* Imagem/Vídeo do exercício */}
            <div className="rounded-lg sm:rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
              {renderExerciseMedia(ex)}
            </div>
            <span className="font-medium truncate text-sm sm:text-base flex-1">
              {ex.name}
            </span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="relative w-full flex items-center gap-2">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        className="flex-1 flex items-center justify-between gap-2 rounded-md sm:rounded-lg border border-stroke bg-white px-3 sm:px-4 py-2 sm:py-2.5 text-left text-xs sm:text-sm font-medium outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 active:border-primary dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary touch-manipulation min-h-[36px] sm:min-h-[40px]"
      >
        <span
          className={`flex-1 truncate ${
            hasValue
              ? "text-gray-900 dark:text-white"
              : "text-gray-400 dark:text-gray-500"
          }`}
        >
          {value || placeholder}
        </span>
        <FiChevronDown
          className={`shrink-0 w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {hasValue && (
        <button
          type="button"
          onClick={handleClear}
          className="shrink-0 text-gray-400 active:text-red-500 dark:text-gray-500 dark:active:text-red-400 p-1.5 rounded touch-manipulation"
          title="Limpar"
          aria-label="Limpar"
        >
          ✕
        </button>
      )}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Selecionar exercício"
        className="!p-0 w-full max-w-full sm:max-w-md flex flex-col h-[85dvh] sm:h-[70vh] sm:max-h-[560px]"
      >
        <div className="flex flex-col h-full min-h-0">
          {/* Busca - sticky no topo */}
          <div className="shrink-0 p-4 sm:p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-meta-4/30">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Digite para buscar..."
                autoFocus
                className="w-full rounded-lg sm:rounded-xl border border-stroke bg-white pl-10 sm:pl-12 pr-4 py-3 sm:py-3.5 text-sm sm:text-base font-medium outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-boxdark dark:text-white dark:focus:border-primary touch-manipulation"
              />
            </div>
          </div>

          {/* Lista de exercícios - scrollável */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {renderListContent()}
          </div>
        </div>
      </Modal>
    </div>
  );
};
